import React, { ReactElement, useRef } from 'react';
import classNames from 'classnames';
import { Node } from '../Node';
import { useConnection } from '../Connections/hooks/use-connection';
import { useDrag } from '../Node/hooks/use-drag';
import { getSelectedIcon } from '../../../EditTopology/configuration/components/VirtualMachineEntities/Edit/components/GeneralForm/utils';
import { usePosition } from '../Node/hooks/use-position';
import {
  handleOnNodeDeleteClick,
  renderNodeActionButtonsHelper,
} from '../utils/utils';
import styles from './HardwareNode.module.css';
import { DefaultHardwareNodeDisplay as defaultPosition } from './constants';
import { HardwareNodeProps } from './container';
import {
  determineShouldRenderHwNodeActionButtons,
  handleOnHWEditClick,
  handleOnHWNodeClick,
} from './utils/utils';

export function HardwareNode({
  uid,
  asset,
  isReadOnly,
  setLeftPanelDeviceUid,
  setLeftPanelType,
  expired,
  setDeviceIdToConfigureInDiagram,
  setSelectedNodeToDelete,
  isSnapToGridEnabled,
  isAddingLogicalConnection,
  isDrawingLogicalConnectionFrom,
  setIsDrawingLogicalConnectionFrom,
  iconName,
  ...node
}: HardwareNodeProps): ReactElement {
  const nodeRef = useRef<HTMLDivElement>(null);
  const { redrawNodeConnections } = useConnection();
  const assetUid = asset?.uid;

  const hookProps = {
    defaultPosition,
    display: node.display,
    nodeRef,
    redrawNodeConnections,
    uid,
  };

  usePosition({ ...hookProps });
  useDrag({ ...hookProps, isReadOnly, isSnapToGridEnabled });
  const isHardwareNodeInDiagramView = !isReadOnly && !!asset?.uid;

  const onNodeClick = () => {
    handleOnHWNodeClick({
      expired,
      nodeUid: uid,
      setLeftPanelDeviceUid,
      setLeftPanelType,
    });
  };

  const onDeleteClick = () => {
    handleOnNodeDeleteClick({
      assetType: 'HARDWARE_ITEM',
      name: asset?.name,
      setSelectedNodeToDelete,
      uid: asset?.uid,
    });
  };

  const onEditClick = () => {
    handleOnHWEditClick({
      assetUid,
      isHardwareNodeInDiagramView,
      setDeviceIdToConfigureInDiagram,
    });
  };

  const renderHWNodeActionButtons = () => {
    return renderNodeActionButtonsHelper({
      name: asset?.name || '',
      onDeleteClick,
      onEditClick,
      shouldRender: determineShouldRenderHwNodeActionButtons({
        isAddingLogicalConnection,
        isHardwareNodeInDiagramView,
      }),
    });
  };

  return (
    <Node
      iconName={iconName}
      isDisabled={hookProps.display?.hidden}
      className={classNames(
        styles.root,
        isHardwareNodeInDiagramView && styles.hwNode,
        {
          [styles.addingLogicalConnectionGlow]: isAddingLogicalConnection,
        },
      )}
      ref={nodeRef}
      isReadOnly={isReadOnly}
      onClick={() => onNodeClick()}
      uid={uid}
      isAddingLogicalConnection={isAddingLogicalConnection}
      isDrawingLogicalConnectionFrom={isDrawingLogicalConnectionFrom}
      setIsDrawingLogicalConnectionFrom={setIsDrawingLogicalConnectionFrom}
      isConnectionAllowed={true}
    >
      <p className={styles.title}>{asset?.name}</p>
      <div
        className={styles.actionButtonsWrapper}
        id={`actionButtonsWrapper-${uid}`}
      >
        {renderHWNodeActionButtons()}
      </div>
      <div className={styles.iconsWrapper}>
        {getSelectedIcon(hookProps.display?.icon, uid, styles.glowIcon)}
      </div>
    </Node>
  );
}
